import { useEffect, useMemo, useState } from "react";
import { saveSeoFiles, loadSeoFiles } from "./adminApi";

const EMPTY_STATE = {
  robotsText: "",
  sitemapText: "",
  robotsFile: null,
  sitemapFile: null,
};

function AdminSeoFiles() {
  const [formState, setFormState] = useState(EMPTY_STATE);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [previewUrls, setPreviewUrls] = useState({ robots: "/robots.txt", sitemap: "/sitemap.xml" });

  useEffect(() => {
    async function fetchSeoFiles() {
      try {
        const response = await loadSeoFiles();
        setFormState((prev) => ({
          ...prev,
          robotsText: response.robots_text || "",
          sitemapText: response.sitemap_text || "",
        }));
        setPreviewUrls({
          robots: response.robots_url || "/robots.txt",
          sitemap: response.sitemap_url || "/sitemap.xml",
        });
      } catch (err) {
        setError(err.message || "Unable to load SEO files.");
      }
    }

    fetchSeoFiles();
  }, []);

  const hasFilesSelected = useMemo(
    () => Boolean(formState.robotsFile || formState.sitemapFile),
    [formState.robotsFile, formState.sitemapFile]
  );

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormState((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field) => (event) => {
    setFormState((prev) => ({ ...prev, [field]: event.target.files?.[0] || null }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setMessage("");
    setIsSaving(true);

    try {
      const response = await saveSeoFiles(formState);
      setMessage(response.message || "SEO files saved successfully.");
      setFormState((prev) => ({
        ...prev,
        robotsFile: null,
        sitemapFile: null,
        robotsText: response.robots_text || prev.robotsText,
        sitemapText: response.sitemap_text || prev.sitemapText,
      }));
      setPreviewUrls({
        robots: response.robots_url || previewUrls.robots,
        sitemap: response.sitemap_url || previewUrls.sitemap,
      });
    } catch (err) {
      setError(err.message || "Unable to save SEO files.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="admin-page">
      <div className="admin-page-header">
        <div>
          <p className="eyebrow">SEO Files</p>
          <h2>Robots & Sitemap</h2>
          <p className="admin-page-note">
            Edit or upload `robots.txt` and `sitemap.xml` directly from the admin panel.
          </p>
        </div>
      </div>

      {error && <p className="auth-error">{error}</p>}
      {message && <p className="admin-success-message">{message}</p>}

      <form className="admin-form" onSubmit={handleSubmit} noValidate>
        <div className="form-grid">
          <div className="panel-panel">
            <div className="admin-card editor-block">
              <div className="admin-card-header">
                <div>
                  <p className="eyebrow">robots.txt</p>
                  <h3>Robots File</h3>
                </div>
              </div>
              <div className="panel-block">
                <label htmlFor="robotsText">Edit robots.txt</label>
                <textarea
                  id="robotsText"
                  value={formState.robotsText}
                  onChange={handleChange("robotsText")}
                  placeholder="User-agent: *\nDisallow: /admin/"
                  rows={12}
                />
              </div>
              <div className="panel-block">
                <label htmlFor="robotsFile">Upload robots.txt</label>
                <input
                  id="robotsFile"
                  type="file"
                  accept="text/plain"
                  onChange={handleFileChange("robotsFile")}
                />
              </div>
              <div className="panel-block">
                <a href={previewUrls.robots} target="_blank" rel="noreferrer">
                  Preview robots.txt
                </a>
              </div>
            </div>
          </div>

          <div className="panel-panel">
            <div className="admin-card editor-block">
              <div className="admin-card-header">
                <div>
                  <p className="eyebrow">sitemap.xml</p>
                  <h3>Sitemap File</h3>
                </div>
              </div>
              <div className="panel-block">
                <label htmlFor="sitemapText">Edit sitemap.xml</label>
                <textarea
                  id="sitemapText"
                  value={formState.sitemapText}
                  onChange={handleChange("sitemapText")}
                  placeholder={"<urlset xmlns=\"http://www.sitemaps.org/schemas/sitemap/0.9\">...</urlset>"}
                  rows={12}
                />
              </div>
              <div className="panel-block">
                <label htmlFor="sitemapFile">Upload sitemap.xml</label>
                <input
                  id="sitemapFile"
                  type="file"
                  accept="application/xml,text/xml"
                  onChange={handleFileChange("sitemapFile")}
                />
              </div>
              <div className="panel-block">
                <a href={previewUrls.sitemap} target="_blank" rel="noreferrer">
                  Preview sitemap.xml
                </a>
              </div>
            </div>
          </div>
        </div>

        <div className="add-blog-actions">
          <button type="submit" className="admin-button primary" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save SEO Files"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminSeoFiles;
